/**
 * Lightweight Analytics + Funnel-Drop-off Tracking
 *
 * Rolle:
 *  - Zentraler Event-Hub (console + dataLayer + sessionStorage-Breadcrumb)
 *  - GTM/Meta/Segment-ready: Events landen im `window.dataLayer`
 *  - Session-Breadcrumb: letzte 40 Events + Timestamps → beim Unload als
 *    „funnel_drop_off"-Event abgesetzt (Kontext für spätere Analyse).
 *  - Kein PII: nur numerische Werte + Enum-Strings.
 */

const BREADCRUMB_KEY = 'fuelx_funnel_breadcrumb_v1';
const MAX_BREADCRUMB = 40;
const DEBUG = typeof window !== 'undefined' && (
  window.location?.hostname === 'localhost' ||
  window.location?.search?.includes('debug=1')
);

function nowIso() {
  return new Date().toISOString();
}

function readBreadcrumb() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.sessionStorage?.getItem(BREADCRUMB_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeBreadcrumb(trail) {
  if (typeof window === 'undefined') return;
  try {
    const clipped = trail.slice(-MAX_BREADCRUMB);
    window.sessionStorage?.setItem(BREADCRUMB_KEY, JSON.stringify(clipped));
  } catch {
    /* noop */
  }
}

/**
 * trackEvent — universeller Event-Sender.
 * @param {string} name — snake_case Event-Name (z. B. "configurator_km_change")
 * @param {object} [props] — nummerische / enum Werte (kein PII)
 */
export function trackEvent(name, props = {}) {
  if (typeof window === 'undefined') return;

  const payload = { event: name, ts: nowIso(), ...props };

  // 1) GTM/Meta/Segment-Layer
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);

  // 2) Session-Breadcrumb
  const trail = readBreadcrumb();
  trail.push(payload);
  writeBreadcrumb(trail);

  // 3) Debug-Console (nur local/debug)
  if (DEBUG) {
    console.debug('[fuelx-analytics]', name, props);
  }
}

/** Aktuelle Breadcrumb-Trail auslesen (z. B. für Debugging / Admin-Panel). */
export function getBreadcrumb() {
  return readBreadcrumb();
}

/**
 * Registriert einmalig einen `beforeunload`-Listener + `visibilitychange`-Fallback
 * (iOS Safari feuert `beforeunload` unzuverlässig). Beides schiebt die letzte
 * bekannte Funnel-Stufe + Breadcrumb als `funnel_drop_off` in den dataLayer.
 */
let unloadInstalled = false;
export function installFunnelDropOffTracker() {
  if (typeof window === 'undefined' || unloadInstalled) return;
  unloadInstalled = true;

  const emit = () => {
    const trail = readBreadcrumb();
    const last = trail[trail.length - 1];
    if (!last) return;
    // Reine drop-off-Signale — Purchase-Completion sendet vorher `purchase_success`
    if (last.event === 'purchase_success') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'funnel_drop_off',
      ts: nowIso(),
      last_event: last.event,
      last_context: {
        step: last.step,
        mode: last.mode,
        km: last.km,
        vehicles: last.vehicles,
      },
      trail_length: trail.length,
    });
  };

  window.addEventListener('beforeunload', emit);
  // iOS Safari + Mobile: beforeunload feuert nicht zuverlässig — visibilitychange schon.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') emit();
  });
}

/**
 * Debounce-Helper — verhindert Slider-Spam (jeder Pixel-Move loggt sonst).
 */
export function debounce(fn, wait = 400) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
}
